import React, { useMemo, useState, useEffect } from "react";
import { baseurl } from "../../Utils/Constants";
import { MdOpenInNew, MdFullscreen, MdFullscreenExit } from "react-icons/md";

// Live Voice Reading Highlight Helper
const ACTIVE_WORD_STYLE = {
    backgroundColor: '#fde047',
    color: '#0f172a',
    fontWeight: 700,
    borderRadius: '4px',
    padding: '2px 5px',
    margin: '0 -1px',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.22), 0 0 0 1.5px #eab308',
    display: 'inline-block',
    lineHeight: '1.35',
    transition: 'background-color 0.1s ease',
};

function renderWords(rawText, keyPrefix, activeWordIndex, wordCounter) {
    if (!rawText || activeWordIndex === undefined || activeWordIndex === null || activeWordIndex < 0 || !wordCounter) {
        return rawText;
    }
    const tokens = rawText.split(/(\s+)/);
    return tokens.map((token, idx) => {
        if (/^\s+$/.test(token) || !token) {
            return token;
        }
        const wIdx = wordCounter.current++;
        if (wordCounter.collect) wordCounter.collect.push(token);
        const isActive = wIdx === activeWordIndex;
        const isRead = wIdx < activeWordIndex;
        return (
            <span
                key={`${keyPrefix}-w${wIdx}`}
                id={isActive ? "hc-aic-active-word" : undefined}
                className={`hc-aic-speaking-word${isActive ? " is-active" : ""}${isRead ? " is-read" : ""}`}
                style={isActive ? ACTIVE_WORD_STYLE : undefined}
            >
                {token}
            </span>
        );
    });
}

// Inline parser – handles **bold** and *italic*
const BOLD_RE = /\*\*([^\*\n]+)\*\*/g;
const ITALIC_RE = /\*([^\*\n]+)\*/g;

function parseItalic(text, keyPrefix, activeWordIndex, wordCounter) {
    const result = [];
    let last = 0;
    let m;
    ITALIC_RE.lastIndex = 0;
    while ((m = ITALIC_RE.exec(text)) !== null) {
        if (m.index > last) {
            const pre = renderWords(text.slice(last, m.index), `${keyPrefix}-ipre${m.index}`, activeWordIndex, wordCounter);
            if (Array.isArray(pre)) result.push(...pre);
            else result.push(pre);
        }
        const inner = renderWords(m[1], `${keyPrefix}-iin${m.index}`, activeWordIndex, wordCounter);
        result.push(
            <em key={`${keyPrefix}-i${m.index}`}>
                {inner}
            </em>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) {
        const post = renderWords(text.slice(last), `${keyPrefix}-ipost`, activeWordIndex, wordCounter);
        if (Array.isArray(post)) result.push(...post);
        else result.push(post);
    }
    if (!result.length) {
        const fallback = renderWords(text, keyPrefix, activeWordIndex, wordCounter);
        return Array.isArray(fallback) ? fallback : [fallback];
    }
    return result;
}

function parseInline(text, keyPrefix, activeWordIndex, wordCounter) {
    const result = [];
    let last = 0;
    let m;
    BOLD_RE.lastIndex = 0;
    while ((m = BOLD_RE.exec(text)) !== null) {
        if (m.index > last) {
            result.push(...parseItalic(text.slice(last, m.index), `${keyPrefix}-pre${m.index}`, activeWordIndex, wordCounter));
        }
        result.push(
            <strong key={`${keyPrefix}-b${m.index}`}>
                {parseItalic(m[1], `${keyPrefix}-bin${m.index}`, activeWordIndex, wordCounter)}
            </strong>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) {
        result.push(...parseItalic(text.slice(last), `${keyPrefix}-post`, activeWordIndex, wordCounter));
    }
    return result.length ? result : parseItalic(text, keyPrefix, activeWordIndex, wordCounter);
}

// Table parser
function parseTable(lines) {
    const rows = lines.map((l) =>
        l
            .replace(/^[\s|]+|[\s|]+$/g, "")
            .split("|")
            .map((c) => c.trim())
    );
    if (rows.length < 2) return null;
    const headers = rows[0];
    const bodyRows = rows.slice(2).filter((r) => r.some((c) => c !== ""));
    return { headers, bodyRows };
}

// Block splitter
function splitBlocks(content) {
    const lines = content.split("\n");
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const raw = lines[i];
        const trimmed = raw.trimStart();

        // --- Markdown Image ![alt](url) ---
        const imgMatch = trimmed.trim().match(/^!\[(.*?)\]\(([^\s)]+)\)$/);
        if (imgMatch) {
            blocks.push({ type: "image", alt: imgMatch[1], url: imgMatch[2] });
            i++;
            continue;
        }

        // --- Table ---
        if (trimmed.startsWith("|")) {
            const tableLines = [];
            while (i < lines.length && lines[i].trimStart().startsWith("|")) {
                tableLines.push(lines[i]);
                i++;
            }
            blocks.push({ type: "table", lines: tableLines });
            continue;
        }

        // --- Heading ---
        const hMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
        if (hMatch) {
            blocks.push({ type: "heading", level: hMatch[1].length, text: hMatch[2] });
            i++;
            continue;
        }

        // --- Bullet list ---
        if (/^[-*\u2022]\s/.test(trimmed)) {
            const items = [];
            while (i < lines.length && /^[-*\u2022]\s/.test(lines[i].trimStart())) {
                items.push(lines[i].trimStart().replace(/^[-*\u2022]\s+/, ""));
                i++;
            }
            blocks.push({ type: "list", items });
            continue;
        }

        // --- Plain text / blank ---
        const textLines = [];
        while (
            i < lines.length &&
            !lines[i].trimStart().startsWith("|") &&
            !/^(#{1,3})\s/.test(lines[i].trimStart()) &&
            !/^[-*\u2022]\s/.test(lines[i].trimStart()) &&
            !/^!\[(.*?)\]\(([^\s)]+)\)$/.test(lines[i].trimStart())
        ) {
            textLines.push(lines[i]);
            i++;
        }
        const text = textLines.join("\n").trim();
        if (text) blocks.push({ type: "text", text });
    }

    return blocks;
}

// Text segments of a block, in the order the block renders them (each is run through parseInline)
function getBlockSegments(block) {
    switch (block.type) {
        case "table": {
            const table = parseTable(block.lines);
            return table ? [...table.headers, ...table.bodyRows.flat()] : [block.lines.join("\n")];
        }
        case "heading":
        case "text":
            return [block.text];
        case "list":
            return block.items;
        default:
            return [];
    }
}

// Display words per segment, tokenised exactly as renderWords numbers them
function collectSegmentWords(segment) {
    const counter = { current: 0, collect: [] };
    parseInline(segment, "collect", 0, counter);
    return counter.collect;
}

// Words of a message in display order, grouped by segment (heading, list item, paragraph, table cell).
// Voice reading speaks exactly these words so the spoken word index always matches the highlighted one.
export function getSpeakableSegments(content) {
    return splitBlocks(content || "")
        .flatMap(getBlockSegments)
        .map(collectSegmentWords)
        .filter((words) => words.length > 0);
}

// Renderers per block type. Each block numbers its words from a fixed startIndex with a counter
// created during its own render, so re-renders (e.g. StrictMode double render) never shift indices.
function TableBlock({ lines, bIdx, activeWordIndex, startIndex }) {
    const table = parseTable(lines);
    if (!table) return <TextBlock text={lines.join("\n")} bIdx={bIdx} activeWordIndex={activeWordIndex} startIndex={startIndex} />;
    const wordCounter = { current: startIndex };
    return (
        <div className="hc-msg-table-wrap">
            <table className="hc-msg-table">
                <thead>
                    <tr>
                        {table.headers.map((h, hi) => (
                            <th key={hi}>{parseInline(h, `${bIdx}-h${hi}`, activeWordIndex, wordCounter)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {table.bodyRows.map((row, ri) => (
                        <tr key={ri} className={ri % 2 === 0 ? "hc-msg-tr-even" : ""}>
                            {row.map((cell, ci) => (
                                <td key={ci}>{parseInline(cell, `${bIdx}-r${ri}c${ci}`, activeWordIndex, wordCounter)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function HeadingBlock({ level, text, bIdx, activeWordIndex, startIndex }) {
    const Tag = level === 1 ? "h4" : level === 2 ? "h5" : "h6";
    const wordCounter = { current: startIndex };
    return <Tag className="hc-msg-heading">{parseInline(text, `${bIdx}-hd`, activeWordIndex, wordCounter)}</Tag>;
}

function ListBlock({ items, bIdx, activeWordIndex, startIndex }) {
    const wordCounter = { current: startIndex };
    return (
        <ul className="hc-msg-list">
            {items.map((item, ii) => (
                <li key={ii}>{parseInline(item, `${bIdx}-li${ii}`, activeWordIndex, wordCounter)}</li>
            ))}
        </ul>
    );
}

function TextBlock({ text, bIdx, activeWordIndex, startIndex }) {
    const wordCounter = { current: startIndex };
    return <p className="hc-msg-text">{parseInline(text, `${bIdx}-t`, activeWordIndex, wordCounter)}</p>;
}

function ImageBlock({ alt, url, imageToken }) {
    const [zoomed, setZoomed] = useState(false);
    const [blobSrc, setBlobSrc] = useState(null);
    const [loading, setLoading] = useState(true);

    // The Teams tab has no Aegis login, so it passes its Teams sign-on token instead.
    const token = imageToken ?? (typeof window !== "undefined"
        ? (localStorage.getItem("accesstoken") || sessionStorage.getItem("accesstoken") || "")
        : "");

    // Resolve API URL
    const resolvedUrl = useMemo(() => {
        if (!url) return "";
        let path = url.trim();
        const apiIdx = path.indexOf("/api/");
        if (apiIdx !== -1) {
            path = path.slice(apiIdx);
        }
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const cleanBase = (baseurl || "").replace(/\/api\/?$/, "");
        const cleanPath = path.startsWith("/") ? path : "/" + path;
        return cleanBase + cleanPath;
    }, [url]);

    // Construct authenticated URL for direct link/new-tab opening
    const authenticatedTabUrl = useMemo(() => {
        if (!resolvedUrl) return "#";
        if (token && resolvedUrl.includes("/api/")) {
            const sep = resolvedUrl.includes("?") ? "&" : "?";
            return `${resolvedUrl}${sep}token=${encodeURIComponent(token)}`;
        }
        return resolvedUrl;
    }, [resolvedUrl, token]);

    // Fetch binary bytes and convert to in-memory Blob URL to keep backend/storage 100% confidential
    useEffect(() => {
        let objectUrl = null;
        let isMounted = true;

        if (!resolvedUrl) {
            setLoading(false);
            return;
        }

        const fetchBinaryImage = async () => {
            try {
                setLoading(true);
                const headers = {};
                if (token) {
                    headers["Authorization"] = `Bearer ${token}`;
                }
                const response = await fetch(resolvedUrl, { headers });
                if (!response.ok) {
                    throw new Error(`Failed to load image: ${response.status}`);
                }
                const blob = await response.blob();
                if (isMounted) {
                    objectUrl = URL.createObjectURL(blob);
                    setBlobSrc(objectUrl);
                }
            } catch (err) {
                console.warn("[ImageBlock] Binary proxy fetch error, falling back to direct URL:", err);
                if (isMounted) {
                    setBlobSrc(authenticatedTabUrl);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchBinaryImage();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [resolvedUrl, token, authenticatedTabUrl]);

    return (
        <div className="my-2" style={{ maxWidth: zoomed ? "100%" : "380px", transition: "max-width 0.25s ease" }}>
            <div 
                className="border rounded-3 overflow-hidden shadow-sm"
                style={{ background: "#f8fafc" }}
            >
                {loading ? (
                    <div className="d-flex align-items-center justify-content-center p-4 text-muted" style={{ minHeight: "160px" }}>
                        <div className="spinner-border spinner-border-sm text-secondary me-2" role="status" />
                        <small>Loading document image...</small>
                    </div>
                ) : (
                    <div
                        style={{ cursor: "pointer" }}
                        onClick={() => setZoomed(!zoomed)}
                        title="Click to toggle image size"
                    >
                        <img
                            src={blobSrc || authenticatedTabUrl}
                            alt={alt || "Document Image"}
                            style={{
                                width: "100%",
                                height: "auto",
                                maxHeight: zoomed ? "650px" : "240px",
                                objectFit: "contain",
                                display: "block",
                                background: "#fff"
                            }}
                            loading="lazy"
                        />
                    </div>
                )}
                {alt ? (
                    <div className="p-2 d-flex justify-content-between align-items-center bg-white border-top">
                        <small className="text-muted text-truncate me-2 fw-medium" title={alt}>{alt}</small>
                        <div className="d-flex align-items-center gap-1">
                            <span 
                                className="badge bg-light text-secondary border d-inline-flex align-items-center gap-1" 
                                style={{ fontSize: "10px", cursor: "pointer", userSelect: "none" }}
                                onClick={() => setZoomed(!zoomed)}
                                title={zoomed ? "Shrink preview" : "Expand preview"}
                            >
                                {zoomed ? <MdFullscreenExit size={12} /> : <MdFullscreen size={12} />}
                                {zoomed ? "Shrink" : "Expand"}
                            </span>
                            <a
                                href={authenticatedTabUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="badge bg-light text-secondary border text-decoration-none d-inline-flex align-items-center justify-content-center"
                                style={{ fontSize: "10px", padding: "4px 6px" }}
                                title="Open full image in new tab"
                            >
                                <MdOpenInNew size={11} />
                            </a>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// Main export
export default function ChatMessageRenderer({ content, isStreaming, activeWordIndex = -1, imageToken }) {
    const blocks = useMemo(() => splitBlocks(content || ""), [content]);
    // First word index of each block, so word numbering never depends on render order or count
    const blockStarts = useMemo(() => {
        let next = 0;
        return blocks.map((block) => {
            const start = next;
            next += getBlockSegments(block).reduce((sum, segment) => sum + collectSegmentWords(segment).length, 0);
            return start;
        });
    }, [blocks]);

    useEffect(() => {
        if (activeWordIndex >= 0) {
            const el = document.getElementById("hc-aic-active-word");
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
            }
        }
    }, [activeWordIndex]);

    return (
        <div className={`hc-msg-renderer${isStreaming ? " hc-msg-streaming" : ""}`}>
            {blocks.map((block, bi) => {
                switch (block.type) {
                    case "table":
                        return <TableBlock key={bi} lines={block.lines} bIdx={bi} activeWordIndex={activeWordIndex} startIndex={blockStarts[bi]} />;
                    case "heading":
                        return <HeadingBlock key={bi} level={block.level} text={block.text} bIdx={bi} activeWordIndex={activeWordIndex} startIndex={blockStarts[bi]} />;
                    case "list":
                        return <ListBlock key={bi} items={block.items} bIdx={bi} activeWordIndex={activeWordIndex} startIndex={blockStarts[bi]} />;
                    case "image":
                        return <ImageBlock key={bi} alt={block.alt} url={block.url} imageToken={imageToken} />;
                    case "text":
                        return <TextBlock key={bi} text={block.text} bIdx={bi} activeWordIndex={activeWordIndex} startIndex={blockStarts[bi]} />;
                    default:
                        return null;
                }
            })}
        </div>
    );
}
